import { useGateValue } from "@statsig/react-bindings";
import { format } from "date-fns";
import { CornerDownRight } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useMarkMessageAsRead } from "../../hooks/useReadState.tsx";
import { cn } from "../../lib/utils.ts";
import { autoActionText } from "@/shared/autoActionText.ts";
import {
  type AutoMessageType,
  autoMessageTypes,
  type GetMessageResp,
  userMessageTypes,
} from "../../types/messages.ts";
import type { ThreadEventBase } from "../../types/threadEvents.ts";
import { USER_REACTIONS_GATE } from "../../types/reactions.ts";
import { ArtifactGroup } from "../artifacts/ArtifactGroup.tsx";
import {
  artifactsHaveBreadcrumbs,
  messageHasRenderableArtifacts,
  isStreamingReplyPlaceholder,
  messageIsTextOnlyBreadcrumb,
} from "../artifacts/artifactUtils.ts";
import { canReactToMessage } from "./canReactToMessage.ts";
import { ReactionBadge } from "./ReactionBadge.tsx";
import { ActionBar } from "../common/ActionBar/ActionBar.tsx";
import { Markdown } from "../markdown/Markdown.tsx";
import { AdminDebugPanel } from "./AdminDebugPanel.tsx";
import { autoActionIcon } from "./autoActionIcon.ts";
import { PendingAttachmentChips } from "./PendingAttachmentChips.tsx";
import type { UserRequestArtifact } from "../../types/artifacts.ts";
import type { SendMessageFn } from "./hooks/useMessageList.tsx";
import { MessageProvider, useMessageContext } from "./MessageContext.tsx";
import { useRelocatedBiscuitIds, useTurnStepsSlot } from "./relocatedBiscuitContext.tsx";
import { ArtifactItem } from "../artifacts/ArtifactItem.tsx";
import { ChannelIcon } from "../artifacts/ResponseArtifactItem.tsx";
import { InboundTextLine } from "./InboundText.tsx";
import { inboundTextOf } from "@/shared/inboundText.ts";

type MessageItemProps = {
  message: GetMessageResp;
  /**
   * ISO-8601 sortDate from the wrapping `MessageThreadEvent`. Lives on the
   * event, not the message — message-level sort_date is being retired
   * (BIG-4700) in favor of the event's ordering key.
   */
  sortDate: string;
  clientOnly?: ThreadEventBase["clientOnly"];
  onDelete: (messageId: number) => void;
  sendMessage: SendMessageFn;
  isUserMessage?: boolean;
  isLastMessage: boolean;
  hideFooter?: boolean;
  /**
   * Emoji left on this message, oldest first: okthx's on a user message, drawn
   * on the bubble's corner, and the user's on one of okthx's replies, drawn by
   * the reply itself.
   */
  reactions?: readonly string[];
  /** A feed nobody can act on, like a shared snapshot: no reaction picker. */
  readOnly?: boolean;
};

type AutoMessageItemProps = MessageItemProps & {
  message: GetMessageResp & {
    type: AutoMessageType;
  };
};

const MessageBubble = ({
  children,
  isUserMessage,
  reactions,
}: {
  children: React.ReactNode;
  isUserMessage?: boolean;
  reactions?: readonly string[];
}) => {
  if (isUserMessage) {
    const hasReactions = !!reactions && reactions.length > 0;
    return (
      <div
        className={cn(
          "relative ml-auto w-fit max-w-[75%] @md:max-w-[87%]",
          "px-5 py-3 bg-accent text-accent-foreground rounded-3xl",
          // Room below the bubble for the badge that overhangs its corner.
          hasReactions && "mb-3",
        )}
      >
        {children}
        {hasReactions && <ReactionBadge reactions={reactions} />}
      </div>
    );
  }

  // else, we don't want a visible bubble, just return as-is
  return <>{children}</>;
};

const MessageItemWrapper = ({
  message,
  onDelete,
  isUserMessage,
  useTightSpacing = false,
  children,
}: {
  message: GetMessageResp;
  onDelete: (messageId: number) => void;
  isUserMessage: boolean;
  useTightSpacing?: boolean;
  children: React.ReactNode;
}) => {
  const [readRef] = useMarkMessageAsRead(message.id, message.isRead ?? false);
  const [isDebugOpen, setIsDebugOpen] = useState(false);

  const toggleDebug = useCallback(() => {
    setIsDebugOpen((prev) => !prev);
  }, []);

  return (
    <div className={cn("group/message relative", useTightSpacing ? "pb-1" : "pb-6 @md:pb-5")}>
      <AdminDebugPanel
        message={message}
        isDebugOpen={isDebugOpen}
        onToggleDebug={setIsDebugOpen}
        onDelete={onDelete}
        isUserMessage={isUserMessage}
      />
      <div
        ref={readRef}
        id={`message-${message.id}`}
        className="flex flex-col flex-1 gap-2"
        onDoubleClick={toggleDebug}
      >
        {children}
      </div>
    </div>
  );
};

const AutoMessageItem = ({ message, sortDate, sendMessage, onDelete }: AutoMessageItemProps) => {
  const Icon = autoActionIcon[message.type];
  const text = autoActionText[message.type];
  const timeStr = useMemo(() => {
    return sortDate ? format(sortDate, "MMM d, h:mm a") : undefined;
  }, [sortDate]);

  // The card this action operated on travels to the turn's answer with the rest
  // of okthx's output. What's left would be a breadcrumb labelled with the same
  // copy as the bubble above it, so drop the group entirely.
  const relocatedIds = useRelocatedBiscuitIds();
  const inlineArtifacts = message.artifacts?.filter((a) => !relocatedIds.has(a.id)) ?? [];
  const hasArtifacts = inlineArtifacts.length > 0;

  return (
    <MessageItemWrapper message={message} onDelete={onDelete} isUserMessage>
      <div className="w-full">
        <MessageBubble isUserMessage>
          <div className="flex flex-row items-center gap-2 justify-end text-sm @md:text-base">
            <Icon className="w-5 h-5" />
            <em>{text}</em>
          </div>
        </MessageBubble>
      </div>
      {hasArtifacts && (
        // Same collapsible-breadcrumb behavior the agent-message path uses,
        // so a heavy attachment (e.g. the `communicationReceived` from a
        // `handleAgentCC` auto-message) starts collapsed and the user can
        // expand to read the email. `messageContent={text}` reuses the
        // auto-action's breadcrumb copy as the toggle label.
        <ArtifactGroup
          messageId={message.id}
          threadId={message.threadId}
          artifacts={inlineArtifacts}
          messageContent={text}
          sendMessage={sendMessage}
          shouldStreamContent={false}
          hideFooter={true}
        />
      )}
      <div className="text-xs text-muted-foreground ml-auto visible hover-device:invisible hover-device:group-hover/message:visible">
        {timeStr}
      </div>
    </MessageItemWrapper>
  );
};

/** Render markdown content, with error handling. */
const MessageItemMarkdown = ({
  children,
  userIsAdmin,
  isStreaming,
  textClassName,
  untrusted,
}: {
  children: string;
  userIsAdmin: boolean | undefined;
  isStreaming: boolean;
  /** Override prose styling */
  textClassName?: string;
  /** Someone other than the user or okthx wrote it; see `MarkdownContextType.untrusted`. */
  untrusted?: boolean;
}) => {
  const { message } = useMessageContext();
  const messageClasses = cn([
    "max-w-full",
    // Single-spaced and nearly flush by design: answer echoes read back a
    // question with the picks bulleted under it. Default prose margins would
    // open a full blank line around the list, and default list padding would
    // push the markers well past the question's left edge — pl-3.5 keeps the
    // marker a few pixels inside the parent copy, text hanging just after it.
    textClassName ??
      "prose prose-zinc text-sm @md:text-base prose-p:my-0 prose-ul:my-0 prose-li:my-0 prose-ul:pl-3.5 prose-ol:pl-3.5 prose-li:pl-0.5",
  ]);

  return (
    <ErrorBoundary
      onError={(err) => {
        console.error("Caught error trying to render children:", children, err);
      }}
      fallbackRender={({ error }) => (
        <div className={messageClasses}>
          Error rendering message - please try again.
          {userIsAdmin && (
            <div className="text-xs text-muted-foreground admin-only bg-red-100 p-4 rounded-lg overflow-x-scroll">
              <div>
                <span className="text-destructive">Error:</span>{" "}
                {error instanceof Error ? error.message : String(error)}
              </div>
              <pre>{children}</pre>
            </div>
          )}
        </div>
      )}
    >
      <Markdown
        className={messageClasses}
        isStreaming={isStreaming}
        message={message}
        // Message content arrives whole — a streamed reply carries a
        // `userResponse` artifact and renders through `ResponseArtifactItem`
        // instead. `isStreaming` here only asks for the enter animation, and it
        // stays set for the rest of the session, so letting it gate the copy
        // buttons would strip them from every message received live.
        isTextStillArriving={false}
        untrusted={untrusted}
      >
        {children}
      </Markdown>
    </ErrorBoundary>
  );
};

const UserMessageItem = ({
  message,
  sortDate,
  sendMessage,
  userIsAdmin,
  onDelete,
  clientOnly,
  reactions,
}: Omit<MessageItemProps, "isUserMessage" | "isLastMessage"> & {
  userIsAdmin: boolean;
}) => {
  // user request artifacts are special - we essentially render them as message content
  // other artifacts are rendered normally
  const userRequestArtifact = message.artifacts?.find(
    (a): a is UserRequestArtifact => a.artifactType === "userRequest",
  );
  const displayContent = userRequestArtifact?.content ?? message.content;
  // A biscuit anchored under the latest response renders only there. User
  // messages carry no breadcrumb, so it simply leaves.
  const relocatedIds = useRelocatedBiscuitIds();
  const displayArtifacts = message.artifacts?.filter(
    (a) => a.artifactType !== "userRequest" && !relocatedIds.has(a.id),
  );

  return (
    <MessageItemWrapper message={message} onDelete={onDelete} isUserMessage>
      {displayContent && message.shouldDisplayContent && (
        <div className="w-full">
          <MessageBubble isUserMessage reactions={reactions}>
            <MessageItemMarkdown userIsAdmin={userIsAdmin} isStreaming={false}>
              {displayContent}
            </MessageItemMarkdown>
          </MessageBubble>
        </div>
      )}
      {/* Provisional chips for an in-flight attachment send, shown until the
          real file artifacts arrive from the summarize job. */}
      <PendingAttachmentChips threadId={message.threadId} clientChatId={clientOnly?.clientChatId} />
      {displayArtifacts && displayArtifacts.length > 0 && (
        // Don't use ArtifactGroup - it has a bunch of logic specific to Agent Messages
        // and renders user messages poorly
        <div className="flex flex-col gap-5">
          {displayArtifacts.map((artifact) => (
            <ArtifactItem
              key={artifact.id}
              messageId={message.id}
              threadId={message.threadId}
              msgListArtifact={artifact}
              sendMessage={sendMessage}
              shouldStreamContent={false}
              hideFooter={true} // always hidden for user messages
            />
          ))}
        </div>
      )}
      <ActionBar
        item={{
          date: new Date(sortDate),
          messageId: message.id,
          threadId: message.threadId,
          isUserMessage: true,
          isHeavy: message.artifacts?.some((artifact) => artifact.isHeavy),
          content: displayContent,
        }}
        sendMessage={sendMessage}
        hoverOnly
        rightContent={<ChannelIcon channel={userRequestArtifact?.channel} />}
      />
    </MessageItemWrapper>
  );
};

const AgentMessageItem = ({
  message,
  sortDate,
  clientOnly,
  sendMessage,
  userIsAdmin,
  onDelete,
  hideFooter = false,
}: Omit<MessageItemProps, "isUserMessage" | "isLastMessage"> & {
  userIsAdmin: boolean;
}) => {
  const shouldStreamContent = Boolean(
    clientOnly?.receivedInSession && message.type !== "userRequest",
  );

  const relocatedIds = useRelocatedBiscuitIds();
  // The steps behind this answer sit above it as an eyebrow: what okthx did
  // reads before what it concluded, and stays out of the way until asked for.
  const turnSteps = useTurnStepsSlot(message.id);

  // An aborted streamed reply is delivered (via thread_event:update) only so the
  // client can drop the placeholder — it renders nothing.
  if (message.status === "aborted") {
    return null;
  }

  // in progress messages generally have no content, skipping the rendering entirely helps avoid spacing issues.
  // Exception: a live reply-stream placeholder (inProgress userResponse artifact) renders its growing text.
  if (message.status === "inProgress" && !isStreamingReplyPlaceholder(message)) {
    return null;
  }

  const hasRenderableArtifacts = messageHasRenderableArtifacts(message.artifacts);

  // Text-only breadcrumbs get tighter spacing (4px) so a run reads as one block
  const isBreadcrumb = messageIsTextOnlyBreadcrumb(message);
  // Artifact group breadcrumbs also get tighter spacing for consistency
  const hasArtifactBreadcrumbs = message.artifacts
    ? artifactsHaveBreadcrumbs(message.artifacts)
    : false;
  const inboundText = inboundTextOf(message);
  // A received text is a line of the conversation, not a trace of okthx's work,
  // so it keeps the space a message has.
  const useTightSpacing = !inboundText && (isBreadcrumb || hasArtifactBreadcrumbs);

  const artifacts = message.artifacts ?? [];
  const showsContentBlock = Boolean(
    message.content && message.shouldDisplayContent && !hasRenderableArtifacts,
  );
  // A message whose every artifact has been relocated still renders: the
  // breadcrumb is what marks that the work happened. It drops out only when
  // there is no breadcrumb copy to render either.
  const hasInlineArtifacts = artifacts.some((a) => !relocatedIds.has(a.id));
  const keepsBreadcrumbOnly = artifacts.length > 0 && !hasInlineArtifacts && !!message.content;
  if (!showsContentBlock && !isBreadcrumb && !hasInlineArtifacts && !keepsBreadcrumbOnly) {
    return null;
  }

  return (
    <MessageItemWrapper
      message={message}
      onDelete={onDelete}
      isUserMessage={false}
      useTightSpacing={useTightSpacing}
    >
      {turnSteps}
      {message.content && message.shouldDisplayContent && !hasRenderableArtifacts && (
        // No char-by-char reveal for bare message content (it has no atomic-range
        // metadata to reveal links safely with) — fade the whole block in instead
        // of hard-popping. This content never streams (a streamed reply carries a
        // `userResponse` artifact and takes the ArtifactGroup path), so there's no
        // buffer to suppress a re-fade on remount the way `ResponseArtifactItem` does.
        <div className={cn("w-full", shouldStreamContent && "animate-in fade-in-0 duration-200")}>
          <div className="flex gap-1 text-muted-foreground">
            <CornerDownRight className="size-3.5 stroke-1.5 my-0.5 shrink-0" />
            <MessageItemMarkdown
              userIsAdmin={userIsAdmin}
              isStreaming={shouldStreamContent}
              textClassName="text-xs"
              // Mail and texts from other people: their links only navigate, so
              // a sender cannot plant a one-tap task answer in the owner's thread.
              untrusted={message.type === "externalCommunication"}
            >
              {message.content}
            </MessageItemMarkdown>
          </div>
        </div>
      )}
      {inboundText && <InboundTextLine text={inboundText} />}
      {message.artifacts && message.artifacts.length > 0 && (
        <ArtifactGroup
          messageId={message.id}
          threadId={message.threadId}
          artifacts={message.artifacts}
          // The line above already says who wrote; the card under the toggle
          // is only where the rest of the message lives.
          messageContent={inboundText ? "Details" : message.content}
          sendMessage={sendMessage}
          shouldStreamContent={shouldStreamContent}
          hideFooter={hideFooter}
        />
      )}
      {/* Text-only breadcrumb messages get an ActionBar (no copy action) */}
      {!hideFooter && isBreadcrumb && (
        <ActionBar
          item={{
            date: new Date(sortDate),
            messageId: message.id,
            threadId: message.threadId,
            isUserMessage: false,
            hideCopyAction: true,
          }}
          sendMessage={sendMessage}
          hoverOnly
        />
      )}
    </MessageItemWrapper>
  );
};

// Type guard to ensure type is one of the auto message types
const isAutoMessageType = (
  messageItemProps: MessageItemProps,
): messageItemProps is AutoMessageItemProps => {
  return autoMessageTypes.safeParse(messageItemProps.message.type).success;
};

export const MessageItem = React.memo((props: MessageItemProps) => {
  const isUserMessage = userMessageTypes.safeParse(props.message.type).success;
  const userIsAdmin = useGateValue("internal-features");
  const reactionsEnabled = useGateValue(USER_REACTIONS_GATE);

  const isAuto = isAutoMessageType(props);

  const content = isAuto ? (
    <AutoMessageItem {...props} />
  ) : isUserMessage ? (
    <UserMessageItem {...props} userIsAdmin={userIsAdmin} />
  ) : (
    <AgentMessageItem {...props} userIsAdmin={userIsAdmin} />
  );

  return (
    <MessageProvider
      isLastMessage={props.isLastMessage}
      message={{ id: props.message.id, clientState: props.message.clientState ?? {} }}
      reactions={props.reactions}
      canReact={canReactToMessage(props.message, {
        reactionsEnabled,
        readOnly: props.readOnly ?? false,
      })}
    >
      {content}
    </MessageProvider>
  );
});
